import type { Event } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { equal, isAfter, isNonNil, now } from '@shady-pines/types';
import { useMemo } from 'react';
import { useUserStore } from '../user/user-store';
import { useEventStore } from './event-store';

export const useFilteredEvents = () => {
  return useEventStore((s) => s.getFilteredEvents(), equal);
};

export const usePublicEvents = (filter = true) => {
  const allEvents = useEventStore((s) => s.events, equal);
  const filtered = useFilteredEvents();

  return useMemo(
    () =>
      (filter ? filtered : (allEvents ?? [])).filter(
        (f) => f.status === 'approved' && isAfter(f.end_time, now()),
      ),
    [allEvents, filter, filtered],
  );
};

export const useFeaturedEvents = (filter = true) => {
  const filtered = useFilteredEvents();
  const allEvents = usePublicEvents(filter);

  return useMemo(
    () => (filter ? filtered : allEvents).filter((f) => f.featured),
    [filtered, allEvents, filter],
  );
};

export const useMyEvents = () => {
  const filtered = useFilteredEvents();
  const userId = useUserStore((s) => s.me);
  return useMemo(() => filtered.filter((f) => f.created_by === userId), [filtered, userId]);
};

export const useSubscribedEvents = () => {
  const subscriptionsById = useEventStore((e) => e.eventSubscriptionsById);
  const events = useFilteredEvents();

  return useMemo(
    () => events.filter((e) => subscriptionsById?.[e.id]),
    [subscriptionsById, events],
  );
};

export const useEventCategoriesById = (id: Uuid<Event>) => {
  const categoryIds = useEventStore((s) => s.categoriesByEventId?.[id], equal);
  const allCategories = useEventStore((s) => s.allCategories);

  return useMemo(
    () => (categoryIds?.map((c) => allCategories?.[c]) ?? []).filter(isNonNil),
    [allCategories, categoryIds],
  );
};

export const useEnrichedEvent = (id: Uuid<Event>) => {
  const event = useEventStore((s) => s.events?.find((e) => e.id === id), equal);
  const categories = useEventCategoriesById(id);
  const users = useEventStore((e) => e.featuredUsersByEventId?.[id]);

  return useMemo(() => ({ event, categories, users: users ?? [] }), [event, categories, users]);
};
